import React, { useState, useEffect } from 'react';
import { FaCalendarAlt, FaUserMd, FaClipboardCheck, FaInfoCircle } from 'react-icons/fa';

const initialState = {
  name: '',
  email: '',
  phone: '',
  dob: '',
  date: '',
  time: '',
  service: '',
  doctor: '',
  symptoms: '',
  urgency: 'normal',
  insurance: '',
  previousVisit: '',
  medicalHistory: '',
  currentMedications: '',
  preferredContact: 'email',
};

const services = [
  'General Check-up',
  'Cardiology Consultation',
  'Pediatric Care',
  'Orthopedic Consultation',
  'Diagnostic Tests',
  'Vaccination',
  'Dental Care',
  'Eye Examination',
  'Physical Therapy',
  'Dermatology',
  'Other',
];

const therapists = [
  'Any Available',
  'Dr. Rohit Sharma (Internal Medicine)',
  'Dr. Priya Patel (Pediatrics)',
  'Dr. Amit Verma (Orthopedics)',
  'Dr. Anjali Singh (Cardiology)',
  'Dr. Rajesh Kumar (General Medicine)',
  'Dr. Meera Shah (Dermatology)',
];

const insuranceProviders = [
  'None/Self-Pay',
  'Star Health',
  'HDFC ERGO',
  'National Insurance',
  'New India Assurance',
  'LIC Health Insurance',
  'ICICI Lombard',
  'Max Bupa',
];

const AppointmentBooking = () => {
  const [form, setForm] = useState(initialState);
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // Preload appointments to compute available time slots quickly when a date is picked
    JSON.parse(localStorage.getItem('appointments') || '[]');
  }, []);

  const generateTimeSlots = (selectedDate) => {
    const slots = [];
    const appointments = JSON.parse(localStorage.getItem('appointments') || '[]');
    const bookedTimes = appointments
      .filter(a => a.date === selectedDate)
      .map(a => a.time);

    for (let hour = 9; hour < 17; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        if (!bookedTimes.includes(timeString)) {
          slots.push(timeString);
        }
      }
    }

    return slots;
  };

  const validate = () => {
    const newErrors = {};
    if (!form.name.trim()) newErrors.name = 'Name is required.';
    if (!form.email.match(/^[^@\s]+@[^@\s]+\.[^@\s]+$/)) newErrors.email = 'Valid email required.';
    if (!form.phone.match(/^\+?[0-9\-\s]{7,}$/)) newErrors.phone = 'Valid phone required.';
    if (!form.dob) newErrors.dob = 'Date of birth required.';
    if (!form.date) newErrors.date = 'Appointment date required.';
    if (!form.time) newErrors.time = 'Appointment time required.';
    if (!form.service) newErrors.service = 'Please select a service.';
    if (!form.preferredContact) newErrors.preferredContact = 'Please select preferred contact method.';

    if (form.date && form.time) {
      const appt = new Date(`${form.date}T${form.time}`);
      if (appt < new Date()) newErrors.time = 'Appointment must be in the future.';
    }

    return newErrors;
  };

  const handleChange = e => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
    setErrors({ ...errors, [name]: undefined });

    if (name === 'date') {
      setAvailableSlots(generateTimeSlots(value));
      setForm(prev => ({ ...prev, time: '' }));
    }
  };

  const nextStep = () => {
    // Only validate essential fields for each step
    const requiredFields = step === 1
      ? ['name', 'email', 'phone', 'dob'] // Only essential personal info
      : step === 2
      ? ['date', 'time', 'service'] // Only essential appointment details
      : [];

    const stepErrors = {};
    requiredFields.forEach(field => {
      // Only perform basic required field validation
      if (!form[field] || form[field].trim() === '') {
        stepErrors[field] = `${field.charAt(0).toUpperCase() + field.slice(1).replace(/([A-Z])/g, ' $1').toLowerCase()} is required.`;
      }
      
      // Basic format validation for email only
      if (field === 'email' && form.email && !form.email.includes('@')) {
        stepErrors.email = 'Please enter a valid email address.';
      }
    });

    if (Object.keys(stepErrors).length) {
      setErrors(stepErrors);
      return;
    }

    // Proceed to next step if validation passes
    setStep(step + 1);
    setErrors({});
  };

  const prevStep = () => {
    setStep(step - 1);
    setErrors({});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validation = validate();
    if (Object.keys(validation).length) {
      setErrors(validation);
      return;
    }

    setIsLoading(true);
    setErrors({});

    // Format date and time to ensure they're in the correct format
    const formattedData = {
      ...form,
      // Explicitly map fields to ensure they're named correctly for the API
      name: form.name.trim(),
      email: form.email.trim(),
      phone: form.phone.trim(),
      dob: form.dob,
      date: form.date, // Should be in YYYY-MM-DD format
      time: form.time, // Should be in HH:MM format
      service: form.service,
      // Use both therapist and doctor fields to ensure compatibility
      therapist: form.therapist || form.doctor || 'Any Available',
      doctor: form.therapist || form.doctor || 'Any Available',
      urgency: form.urgency || 'normal',
      preferredContact: form.preferredContact || 'email'
    };

    console.log('Submitting appointment data:', formattedData);

    // Try multiple API endpoints in case one fails
    const maxRetries = 2;
    let success = false;
    let lastError = null;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        // Determine API URL based on environment
        let apiUrl = '/api/booking.php';
        if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
          const port = window.location.port;
          apiUrl = `http://${window.location.hostname}${port ? ':' + port : ''}/api/booking.php`;
          
          // Fallback to alternative endpoint if this is a retry
          if (attempt === 1) {
            apiUrl = `http://${window.location.hostname}${port ? ':' + port : ''}/api/emergency_booking.php`;
          }
        } else if (window.location.hostname === 'healthcare.fliphatmedia.com') {
          apiUrl = 'https://healthcare.fliphatmedia.com/api/booking.php';
          
          // Fallback to alternative endpoint if this is a retry
          if (attempt === 1) {
            apiUrl = 'https://healthcare.fliphatmedia.com/api/emergency_booking.php';
          }
        }
        
        console.log(`Attempt ${attempt + 1}/${maxRetries + 1} - Using API URL:`, apiUrl);
        
        // Send data to backend API
        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formattedData),
          credentials: 'include'
        });

        // Check if response is valid
        if (!response.ok) {
          throw new Error(`Server returned ${response.status}: ${response.statusText}`);
        }

        const result = await response.json();
        console.log('Booking response:', result);

        if (result.success) {
          console.log('Server confirmed successful booking!');
          
          // Success! Save to localStorage for backup/offline functionality
          const appointments = JSON.parse(localStorage.getItem('appointments') || '[]');
          const newAppointment = {
            ...formattedData,
            id: Date.now(),
            status: 'pending',
            createdAt: new Date().toISOString(),
            serverConfirmed: true // Mark as confirmed by server
          };

          appointments.push(newAppointment);
          localStorage.setItem('appointments', JSON.stringify(appointments));
          
          setSubmitted(true);
          setForm(initialState);
          setStep(1);
          success = true;
          break; // Exit the retry loop
        } else {
          lastError = result.error || 'Unknown server error';
          console.warn(`Attempt ${attempt + 1} failed:`, lastError);
          
          // Only retry if this wasn't the last attempt
          if (attempt < maxRetries) {
            await new Promise(resolve => setTimeout(resolve, 500)); // Wait before retry
            continue;
          }
          throw new Error(lastError);
        }
      } catch (err) {
        console.error(`Attempt ${attempt + 1} error:`, err);
        lastError = err.message || 'Network error';
        
        // Log more details about the error
        console.error('Detailed error information:', {
          attemptNumber: attempt + 1,
          formData: formattedData,
          error: err
        });
        
        // Always fallback to localStorage if all server attempts fail
        if (attempt === maxRetries) {
          // Fallback: Save to localStorage even if API fails
          const appointments = JSON.parse(localStorage.getItem('appointments') || '[]');
          const offlineAppointment = {
            ...formattedData,
            id: Date.now(),
            status: 'pending (offline)',
            createdAt: new Date().toISOString(),
            offlineCreated: true
          };

          appointments.push(offlineAppointment);
          localStorage.setItem('appointments', JSON.stringify(appointments));
          
          // Show a different message for offline mode
          setErrors({ 
            submit: 'Server connection failed, but your appointment was saved locally. Please contact us to confirm your booking.'
          });
          
          setSubmitted(true); // Still mark as submitted since we stored it locally
          success = true;
          break;
        }
        
        // Wait before retry
        if (attempt < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }
    }

    if (!success) {
      setErrors({ 
        submit: `Failed to book appointment: ${lastError || 'Unknown error'}`
      });
    }
    
    setIsLoading(false);
  };

  if (submitted) {
    return (
      <div className="bg-purple-50 border border-purple-200 text-purple-700 px-6 py-8 rounded-lg shadow-md mb-6">
        <div className="flex items-center justify-center mb-4">
          <div className="bg-purple-100 p-3 rounded-full">
            <FaClipboardCheck className="text-purple-600 text-3xl" />
          </div>
        </div>
        <h3 className="text-xl font-bold mb-3 text-center">Medical Appointment Confirmed!</h3>
        <p className="text-center mb-4">Thank you for choosing HealthBridge Clinic. Our medical staff will review your information and send you a confirmation with any pre-appointment instructions.</p>
        <p className="text-sm text-purple-600 bg-purple-100 p-3 rounded mb-4">
          <FaInfoCircle className="inline mr-2" />
          A confirmation email has been sent to your email address. Please check your inbox.
        </p>
        <div className="flex justify-center">
          <button
            className="mt-2 px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors duration-300 font-medium"
            onClick={() => setSubmitted(false)}
          >
            Book Another Appointment
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white shadow-lg rounded-lg p-6 space-y-6" id="appointment-booking-container">
      <h2 className="text-2xl font-bold mb-2 text-center text-purple-700">Book a Clinical Appointment</h2>

      <div className="flex justify-center mb-6 sticky top-0 bg-white pb-4 pt-1 z-10">
        <div className="flex items-center w-full max-w-3xl">
          <div className={`flex flex-col items-center ${step >= 1 ? 'text-purple-600' : 'text-gray-400'}`}>
            <div 
              className={`w-10 h-10 flex items-center justify-center rounded-full transition-all duration-300 ${step >= 1 ? 'bg-purple-100 text-purple-600 scale-110' : 'bg-gray-200 text-gray-500'} mb-1`}
              onClick={() => step > 1 && prevStep()}
              role={step > 1 ? "button" : undefined}
            >
              <FaUserMd className="text-lg" />
            </div>
            <span className="text-xs font-medium">Personal Info</span>
          </div>

          <div className={`flex-1 h-1 mx-2 transition-colors duration-500 ${step >= 2 ? 'bg-purple-500' : 'bg-gray-200'}`}></div>

          <div className={`flex flex-col items-center ${step >= 2 ? 'text-purple-600' : 'text-gray-400'}`}>
            <div 
              className={`w-10 h-10 flex items-center justify-center rounded-full transition-all duration-300 ${step >= 2 ? 'bg-purple-100 text-purple-600 scale-110' : 'bg-gray-200 text-gray-500'} mb-1`}
              onClick={() => step > 2 && setStep(2)}
              role={step > 2 ? "button" : undefined}
            >
              <FaCalendarAlt className="text-lg" />
            </div>
            <span className="text-xs font-medium">Schedule</span>
          </div>

          <div className={`flex-1 h-1 mx-2 transition-colors duration-500 ${step >= 3 ? 'bg-purple-500' : 'bg-gray-200'}`}></div>

          <div className={`flex flex-col items-center ${step >= 3 ? 'text-purple-600' : 'text-gray-400'}`}>
            <div className={`w-10 h-10 flex items-center justify-center rounded-full transition-all duration-300 ${step >= 3 ? 'bg-purple-100 text-purple-600 scale-110' : 'bg-gray-200 text-gray-500'} mb-1`}>
              <FaClipboardCheck className="text-lg" />
            </div>
            <span className="text-xs font-medium">Confirmation</span>
          </div>
        </div>
      </div>

      <form onSubmit={step === 3 ? handleSubmit : e => e.preventDefault()} className="space-y-6" style={{ minHeight: '400px' }}>
        {/* Step 1: Personal Information */}
        <div className={`transition-opacity duration-300 ${step === 1 ? 'opacity-100' : 'opacity-0 hidden'}`}>
        {step === 1 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block font-semibold mb-1">Full Name *</label>
              <input
                type="text"
                name="name"
                value={form.name}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 ${errors.name ? 'border-red-500' : 'border-gray-300'}`}
                required
              />
              {errors.name && <div className="text-red-500 text-sm mt-1">{errors.name}</div>}
            </div>
            <div>
              <label className="block font-semibold mb-1">Email *</label>
              <input
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 ${errors.email ? 'border-red-500' : 'border-gray-300'}`}
                required
              />
              {errors.email && <div className="text-red-500 text-sm mt-1">{errors.email}</div>}
            </div>
            <div>
              <label className="block font-semibold mb-1">Phone *</label>
              <input
                type="tel"
                name="phone"
                value={form.phone}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 ${errors.phone ? 'border-red-500' : 'border-gray-300'}`}
                required
              />
              {errors.phone && <div className="text-red-500 text-sm mt-1">{errors.phone}</div>}
            </div>
            <div>
              <label className="block font-semibold mb-1">Date of Birth *</label>
              <input
                type="date"
                name="dob"
                value={form.dob}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 ${errors.dob ? 'border-red-500' : 'border-gray-300'}`}
                required
              />
              {errors.dob && <div className="text-red-500 text-sm mt-1">{errors.dob}</div>}
            </div>
            <div>
              <label className="block font-semibold mb-1">Preferred Contact *</label>
              <select
                name="preferredContact"
                value={form.preferredContact}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 ${errors.preferredContact ? 'border-red-500' : 'border-gray-300'}`}
                required
              >
                <option value="email">Email</option>
                <option value="phone">Phone</option>
              </select>
              {errors.preferredContact && <div className="text-red-500 text-sm mt-1">{errors.preferredContact}</div>}
            </div>
          </div>
        )}
        </div>
        {/* Step 2: Appointment Details */}
        <div className={`transition-opacity duration-300 ${step === 2 ? 'opacity-100' : 'opacity-0 hidden'}`}>
        {step === 2 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block font-semibold mb-1">Appointment Date *</label>
              <input
                type="date"
                name="date"
                value={form.date}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 ${errors.date ? 'border-red-500' : 'border-gray-300'}`}
                required
              />
              {errors.date && <div className="text-red-500 text-sm mt-1">{errors.date}</div>}
            </div>
            <div>
              <label className="block font-semibold mb-1">Appointment Time *</label>
              <select
                name="time"
                value={form.time}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 ${errors.time ? 'border-red-500' : 'border-gray-300'}`}
                required
              >
                <option value="">Select a time</option>
                {availableSlots.map(slot => (
                  <option key={slot} value={slot}>{slot}</option>
                ))}
              </select>
              {errors.time && <div className="text-red-500 text-sm mt-1">{errors.time}</div>}
            </div>
            <div>
              <label className="block font-semibold mb-1">Service *</label>
              <select
                name="service"
                value={form.service}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 ${errors.service ? 'border-red-500' : 'border-gray-300'}`}
                required
              >
                <option value="">Select a service</option>
                {services.map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
              {errors.service && <div className="text-red-500 text-sm mt-1">{errors.service}</div>}
            </div>
            <div>
              <label className="block font-semibold mb-1">Preferred Therapist</label>
              <select
                name="therapist"
                value={form.therapist}
                onChange={handleChange}
                className="w-full px-3 py-2 border rounded border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              >
                {therapists.map(t => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block font-semibold mb-1">Urgency</label>
              <select
                name="urgency"
                value={form.urgency}
                onChange={handleChange}
                className="w-full px-3 py-2 border rounded border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              >
                <option value="normal">Normal</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
            <div>
              <label className="block font-semibold mb-1">Insurance Provider</label>
              <select
                name="insurance"
                value={form.insurance}
                onChange={handleChange}
                className="w-full px-3 py-2 border rounded border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              >
                {insuranceProviders.map(i => (
                  <option key={i} value={i}>{i}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block font-semibold mb-1">Referral Source</label>
              <input
                type="text"
                name="referral"
                value={form.referral}
                onChange={handleChange}
                className="w-full px-3 py-2 border rounded border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                placeholder="(e.g. Doctor, Friend, Website)"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block font-semibold mb-1">Additional Notes</label>
              <textarea
                name="notes"
                value={form.notes}
                onChange={handleChange}
                className="w-full px-3 py-2 border rounded border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                rows={3}
              />
            </div>
          </div>
        )}
        </div>
        {/* Step 3: Confirmation */}
        <div className={`transition-opacity duration-300 ${step === 3 ? 'opacity-100' : 'opacity-0 hidden'}`}>
        {step === 3 && (
          <div className="space-y-4">
            {errors.submit && (
              <div className="bg-red-50 border-l-4 border-red-500 p-4 text-red-700 mb-4">
                <p className="font-bold">Error</p>
                <p>{errors.submit}</p>
              </div>
            )}
            <div className="bg-purple-50 rounded-lg p-5 border border-purple-100">
              <h3 className="text-lg font-semibold mb-4 text-purple-700">Appointment Summary</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div className="space-y-2">
                  <h4 className="font-medium text-purple-700">Personal Information</h4>
                  <div><strong>Name:</strong> {form.name}</div>
                  <div><strong>Email:</strong> {form.email}</div>
                  <div><strong>Phone:</strong> {form.phone}</div>
                  <div><strong>Date of Birth:</strong> {form.dob}</div>
                  <div><strong>Preferred Contact:</strong> {form.preferredContact}</div>
                </div>
                <div className="space-y-2">
                  <h4 className="font-medium text-purple-700">Appointment Details</h4>
                  <div><strong>Date:</strong> {form.date}</div>
                  <div><strong>Time:</strong> {form.time}</div>
                  <div><strong>Service:</strong> {form.service}</div>
                  <div><strong>Therapist:</strong> {form.therapist}</div>
                  <div><strong>Urgency:</strong> {form.urgency}</div>
                </div>
              </div>
              <div className="space-y-2 mb-6">
                <h4 className="font-medium text-purple-700">Additional Information</h4>
                <div><strong>Insurance:</strong> {form.insurance}</div>
                <div><strong>Referral:</strong> {form.referral}</div>
                <div><strong>Notes:</strong> {form.notes || 'No additional notes'}</div>
              </div>
              <div className="flex justify-center gap-4 mt-6">
                <button
                  type="button"
                  onClick={prevStep}
                  className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded text-gray-700 font-medium"
                >
                  Back
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded font-bold flex items-center"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Processing...
                    </>
                  ) : 'Confirm Appointment'}
                </button>
              </div>
            </div>
          </div>
        )}
        </div>
        {/* Navigation Buttons */}
        {step < 3 && (
          <div className="flex justify-end gap-4 mt-6 sticky bottom-4 bg-white pt-2">
            {step > 1 && (
              <button
                type="button"
                onClick={prevStep}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded text-gray-700 font-medium transition-all hover:shadow-md"
              >
                Back
              </button>
            )}
            <button
              type="button"
              onClick={nextStep}
              className="px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded font-bold transition-all hover:shadow-md"
            >
              Next
            </button>
          </div>
        )}
      </form>
    </div>
  );
};

export default AppointmentBooking;